/**
 * The fitting room (ห้องลองชุด): every fashion skin, back skin and piece of gear that changes the
 * look, owned or not, tried on the player's own figure before it is bought or worn. Trying on is
 * only a picture on this screen; wearing still goes through commands, and only for what is owned.
 */
import {
  EQUIP_SLOTS,
  ITEMS,
  itemOf,
  lookOf,
  MALL,
  MONSTERS,
  ownsSkin,
  PASS_TIERS,
  RARITY_LABELS,
  SHOP,
  SKIN_SLOT_LABELS,
  SKINS,
  SLOT_LABELS,
  TOPUP_MILESTONES,
  type EquipSlot,
  type PlayerCommand,
  type PlayerProgress,
  type SkinSlot,
  type World,
} from '@nine/shared';
import { artH, artW, artX, artY, resOf } from '../render/pixel';
import { viewOf, type SpriteBank } from '../render/sprites';
import { h } from './dom';

type FitSlot = EquipSlot | SkinSlot;

/** What is being tried per slot: an id, null for nothing worn there, absent for as worn now. */
export interface Fitting {
  tried: Partial<Record<FitSlot, string | null>>;
  filter: 'all' | FitSlot;
  /** Screen heading the figure faces (2 = towards the viewer). */
  heading: number;
}

export const newFitting = (): Fitting => ({ tried: {}, filter: 'all', heading: 2 });

interface Wearable {
  slot: FitSlot;
  id: string;
  name: string;
  desc: string;
  /** Rarity class and label (gear only). */
  rarity?: string;
  /** Where it comes from, for what is not owned yet. */
  source: string;
  /** The jade-mall entry, if it is sold there. */
  mall: boolean;
}

const FILTERS: readonly ['all' | FitSlot, string][] = [
  ['all', 'ทั้งหมด'],
  ['outfit', SKIN_SLOT_LABELS.outfit],
  ['back', SKIN_SLOT_LABELS.back],
  ['body', 'ชุด'],
  ['head', SLOT_LABELS.head],
  ['weapon', SLOT_LABELS.weapon],
];

const fmt = (n: number) => Math.floor(n).toLocaleString('th-TH');

/** Where a skin is had: the mall, the season pass, top-up milestones. */
function skinSource(id: string): string {
  const parts: string[] = [];
  const m = MALL.find((e) => e.kind === 'skin' && e.id === id);
  if (m) parts.push(`ร้านหยก ${fmt(m.price)} หยก`);
  const tier = PASS_TIERS.find((t) => t.free.skin === id || t.premium.skin === id);
  if (tier) parts.push(`บัตรผ่านฤดูกาล ขั้น ${tier.level}`);
  const top = TOPUP_MILESTONES.find((t) => t.skin === id);
  if (top) parts.push(`ยอดเติมสะสม ${fmt(top.baht)} บาท`);
  return parts.join(' · ') || 'กิจกรรมพิเศษ';
}

/** Where a piece of gear is had: the shop, or the monsters that drop it. */
function itemSource(id: string): string {
  const parts: string[] = [];
  const s = SHOP.find((e) => e.item === id);
  if (s) parts.push(`ร้านค้า ${fmt(s.price)} ตำลึง`);
  const from = Object.values(MONSTERS).filter((m) => m.loot?.some((l) => l.item === id));
  if (from.length) parts.push(`ดรอปจาก ${from.map((m) => m.name).join(', ')}`);
  return parts.join(' · ') || 'รางวัลภารกิจ';
}

const WEARABLES: readonly Wearable[] = [
  ...Object.values(SKINS).map((s) => ({
    slot: s.slot,
    id: s.id,
    name: s.name,
    desc: s.desc,
    source: skinSource(s.id),
    mall: MALL.some((e) => e.kind === 'skin' && e.id === s.id),
  })),
  ...Object.values(ITEMS).map((it) => ({
    slot: it.slot,
    id: it.id,
    name: it.name,
    desc: it.desc,
    rarity: it.rarity,
    source: itemSource(it.id),
    mall: false,
  })),
];

const isSkinSlot = (s: FitSlot): s is SkinSlot => s === 'outfit' || s === 'back';

/** The id of the base item an inventory entry is (rolled items carry their own ids). */
const baseOf = (pr: PlayerProgress, id: string): string => pr.rolls[id]?.base ?? id;

/** An owned copy of a piece of gear (worn first), to equip; or null. */
function ownedCopy(pr: PlayerProgress, w: Wearable): string | null {
  // Skins: bought or granted, or free to everyone (the fashion outfits, data/skins.ts `free`).
  if (isSkinSlot(w.slot)) return ownsSkin(pr.skins, w.id) ? w.id : null;
  const worn = pr.equipment[w.slot as EquipSlot];
  if (worn && baseOf(pr, worn) === w.id) return worn;
  return pr.inventory.find((id) => baseOf(pr, id) === w.id) ?? null;
}

/** What is worn in a slot now: the id (gear by its base item). */
function wornIn(pr: PlayerProgress, slot: FitSlot): string | null {
  if (isSkinSlot(slot)) return pr.skinWorn[slot] ?? null;
  const id = pr.equipment[slot];
  return id ? baseOf(pr, id) : null;
}

/** The look with what is being tried on in place of what is worn. */
function triedLook(
  pr: PlayerProgress,
  fit: Fitting,
): { sprite: string; outfit: string[]; back: string | null } {
  const at = (slot: FitSlot) => (fit.tried[slot] !== undefined ? fit.tried[slot]! : wornIn(pr, slot));
  const outfit: string[] = [];
  for (const slot of EQUIP_SLOTS) {
    const id = at(slot);
    const it = id ? (itemOf(pr.rolls, id) ?? ITEMS[id]) : undefined;
    if (it) outfit.push(it.visual);
  }
  const fashion = at('outfit');
  const look = fashion ? SKINS[fashion]?.look : undefined;
  if (look) outfit.push(look);
  return { sprite: lookOf(pr).sprite, outfit, back: at('back') };
}

export interface FittingHost {
  world: World;
  sprites: SpriteBank | undefined;
  fit: Fitting;
  command: (c: PlayerCommand) => void;
  openMall: () => void;
}

/** The fitting room: the figure on the left, everything to try on the right. */
export function fittingRoom(host: FittingHost): HTMLElement {
  const root = h('div', { class: 'fitting' });
  const stage = h('canvas', { class: 'fit-stage' });
  stage.width = 200;
  stage.height = 240;
  animate(stage, host);
  const side = h('div', { class: 'fit-side' });
  const shelf = h('div', { class: 'fit-shelf' });
  root.append(h('div', { class: 'fit-left' }, stage, side), shelf);

  const render = () => {
    const scroller = root.closest('#panelBody');
    const top = scroller?.scrollTop ?? 0;
    side.replaceChildren(...controls(host, render).filter((x) => x !== null));
    shelf.replaceChildren(...cards(host, render));
    if (scroller) scroller.scrollTop = top;
  };
  render();
  return root;
}

function controls(host: FittingHost, render: () => void): (HTMLElement | null)[] {
  const { fit, world } = host;
  const pr = world.progress;
  const turn = (d: number) =>
    h('button', {
      type: 'button',
      class: 'btn ghost',
      textContent: d < 0 ? '⟲ หมุน' : 'หมุน ⟳',
      onclick: () => {
        fit.heading = (fit.heading + d + 8) % 8;
      },
    });
  const tried = (Object.entries(fit.tried) as [FitSlot, string | null][]).filter(
    ([slot, id]) => id !== wornIn(pr, slot),
  );
  const rows = tried.map(([slot, id]) => {
    const w = WEARABLES.find((x) => x.slot === slot && x.id === id);
    const label = isSkinSlot(slot) ? SKIN_SLOT_LABELS[slot] : SLOT_LABELS[slot];
    if (!w) return h('li', {}, `${label}: ไม่สวม`);
    const owned = ownedCopy(pr, w) !== null;
    return h(
      'li',
      { class: owned ? 'owned' : 'locked' },
      h('b', {}, w.name),
      h('small', {}, owned ? 'มีแล้ว' : `ยังไม่มี · ${w.source}`),
    );
  });
  // Wear what is tried and owned (and take off what was set to nothing).
  const cmds: PlayerCommand[] = [];
  for (const [slot, id] of tried) {
    if (id === null) cmds.push(isSkinSlot(slot) ? { t: 'setSkin', slot, id: null } : { t: 'unequip', slot });
    else {
      const w = WEARABLES.find((x) => x.slot === slot && x.id === id)!;
      const copy = ownedCopy(pr, w);
      if (!copy) continue;
      // A skin goes on through the wardrobe, which knows the free ones (systems/wardrobe.ts `wardrobeWear`).
      cmds.push(isSkinSlot(slot) ? { t: 'wardrobeWear', slot, id: copy } : { t: 'equip', id: copy });
    }
  }
  const locked = tried.some(([slot, id]) => {
    const w = id && WEARABLES.find((x) => x.slot === slot && x.id === id);
    return w && !ownedCopy(pr, w);
  });
  const lockedMall = tried.some(([slot, id]) => {
    const w = id && WEARABLES.find((x) => x.slot === slot && x.id === id);
    return w && w.mall && !ownedCopy(pr, w);
  });
  return [
    h('div', { class: 'fit-turn' }, turn(-1), turn(1)),
    h('h4', {}, tried.length ? 'กำลังลอง' : 'สวมชุดปัจจุบัน'),
    tried.length
      ? h('ul', { class: 'fit-tried' }, ...rows)
      : h('p', { class: 'note' }, 'กดชิ้นทางขวาเพื่อลองสวม'),
    h(
      'div',
      { class: 'fit-actions' },
      h('button', {
        type: 'button',
        class: 'btn',
        disabled: !cmds.length,
        textContent: locked && cmds.length ? 'สวมเฉพาะที่มี' : 'สวมใส่',
        onclick: () => {
          for (const c of cmds) host.command(c);
          for (const [slot] of tried) {
            const id = fit.tried[slot];
            const w = id && WEARABLES.find((x) => x.slot === slot && x.id === id);
            if (id === null || (w && ownedCopy(pr, w))) delete fit.tried[slot];
          }
          render();
        },
      }),
      lockedMall
        ? h('button', {
            type: 'button',
            class: 'btn ghost',
            textContent: 'ไปร้านหยก',
            onclick: host.openMall,
          })
        : null,
      h('button', {
        type: 'button',
        class: 'btn ghost',
        disabled: !Object.keys(fit.tried).length,
        textContent: 'ถอดที่ลองทั้งหมด',
        onclick: () => {
          fit.tried = {};
          render();
        },
      }),
    ),
  ];
}

function cards(host: FittingHost, render: () => void): HTMLElement[] {
  const { fit, world } = host;
  const pr = world.progress;
  const chips = h(
    'div',
    { class: 'fit-filters' },
    ...FILTERS.map(([f, label]) =>
      h('button', {
        type: 'button',
        class: `chip ${fit.filter === f ? 'on' : ''}`,
        textContent: label,
        onclick: () => {
          fit.filter = f;
          render();
        },
      }),
    ),
  );
  const list = WEARABLES.filter((w) => fit.filter === 'all' || w.slot === fit.filter);
  const grid = h(
    'div',
    { class: 'fit-grid' },
    // A bare slot first when one slot is shown: to try it with nothing there.
    fit.filter !== 'all'
      ? h(
          'button',
          {
            type: 'button',
            class: `fit-card bare ${fit.tried[fit.filter] === null ? 'on' : ''}`,
            onclick: () => {
              const slot = fit.filter as FitSlot;
              if (fit.tried[slot] === null) delete fit.tried[slot];
              else fit.tried[slot] = null;
              render();
            },
          },
          h('span', { class: 'fit-none' }, '∅'),
          h('b', {}, 'ไม่สวม'),
        )
      : null,
    ...list.map((w) => {
      const owned = ownedCopy(pr, w) !== null;
      const worn = wornIn(pr, w.slot) === w.id;
      const on = fit.tried[w.slot] === w.id;
      return h(
        'button',
        {
          type: 'button',
          class: `fit-card ${w.rarity ? `r-${w.rarity}` : ''} ${on ? 'on' : ''} ${owned ? '' : 'locked'}`,
          title: w.desc,
          onclick: () => {
            if (on) delete fit.tried[w.slot];
            else fit.tried[w.slot] = w.id;
            render();
          },
        },
        thumb(host, w),
        h('b', {}, w.name),
        h(
          'small',
          {},
          `${isSkinSlot(w.slot) ? SKIN_SLOT_LABELS[w.slot] : SLOT_LABELS[w.slot]}${w.rarity ? ` · ${RARITY_LABELS[w.rarity as keyof typeof RARITY_LABELS]}` : ''}`,
        ),
        h(
          'span',
          { class: `fit-tag ${worn ? 'worn' : owned ? 'owned' : 'locked'}` },
          worn ? 'สวมอยู่' : owned ? 'มีแล้ว' : '🔒 ยังไม่มี',
        ),
      );
    }),
  );
  return [chips, grid];
}

/** The figure wearing just this piece over what is worn now. */
function thumb(host: FittingHost, w: Wearable): HTMLCanvasElement {
  const c = h('canvas', { class: 'fit-thumb' });
  c.width = 72;
  c.height = 72;
  const pr = host.world.progress;
  const look = triedLook(pr, { tried: { [w.slot]: w.id }, filter: 'all', heading: 2 });
  const g = c.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  if (w.slot === 'back') {
    // The back piece seen from behind, where it shows.
    drawFigure(g, host, look, 6, 36, 70, 1);
    return c;
  }
  drawFigure(g, host, look, 2, 36, 70, 1);
  return c;
}

/** Draws the figure at (cx, feetY), `heading` as on the map, with the back skin where it shows. */
function drawFigure(
  g: CanvasRenderingContext2D,
  host: FittingHost,
  look: ReturnType<typeof triedLook>,
  heading: number,
  cx: number,
  feetY: number,
  frame: number,
  fitH = 0,
): void {
  const base = host.sprites?.of(look);
  if (!base) return;
  const { set, facing } = viewOf(base, heading, 1);
  const frames = set.idle;
  const img = frames[frame % frames.length]!;
  const room = fitH || g.canvas.height - 4;
  // Sized by the figure itself (head to feet), not the padded frame it sits in.
  const tall = Math.max(8, set.ay - topOf(img));
  // Frames are padded wide for weapons and sleeves: only the height decides the size.
  const k = room / tall;
  const behind = heading >= 5 && heading <= 7;
  const back = look.back ? backPicture(look.back) : null;
  const drawBack = () => {
    if (!back) return;
    // The fan spans the shoulders, its mount between the shoulder blades.
    const s = (tall * k) / 110;
    g.drawImage(back, cx - 48 * s, feetY - tall * k * 0.78 - 66 * s, 96 * s, 96 * s);
  };
  if (!behind) drawBack();
  g.save();
  g.translate(cx, 0);
  if (facing < 0) g.scale(-1, 1);
  g.drawImage(img, (artX(img) - set.ax) * k, feetY + (artY(img) - set.ay) * k, artW(img) * k, artH(img) * k);
  g.restore();
  if (behind) drawBack();
}

const tops = new WeakMap<HTMLCanvasElement, number>();
/** The first row of a frame with anything drawn on it, in art pixels. */
function topOf(img: HTMLCanvasElement): number {
  let t = tops.get(img);
  if (t === undefined) {
    const d = img.getContext('2d')!.getImageData(0, 0, img.width, img.height).data;
    t = 0;
    find: for (let y = 0; y < img.height; y++)
      for (let x = 0; x < img.width; x++)
        if (d[(y * img.width + x) * 4 + 3]! > 0) {
          t = y;
          break find;
        }
    t = t / resOf(img) + artY(img);
    tops.set(img, t);
  }
  return t;
}

const backPics = new Map<string, HTMLCanvasElement>();
function backPicture(id: string): HTMLCanvasElement | null {
  const sk = SKINS[id];
  if (!sk) return null;
  let c = backPics.get(id);
  if (!c) backPics.set(id, (c = skinPreview(sk.color)));
  return c;
}

/** Keeps the stage drawn (idle breathing, turning) while it is on screen. */
function animate(c: HTMLCanvasElement, host: FittingHost): void {
  let seen = false;
  const g = c.getContext('2d')!;
  const tick = (t: number) => {
    if (c.isConnected) seen = true;
    else if (seen) return;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, c.width, c.height);
    g.imageSmoothingEnabled = false;
    // A round dais under the figure.
    g.fillStyle = 'rgba(232,184,74,.16)';
    g.beginPath();
    g.ellipse(c.width / 2, c.height - 22, 64, 16, 0, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = 'rgba(232,184,74,.5)';
    g.stroke();
    const look = triedLook(host.world.progress, host.fit);
    drawFigure(
      g,
      host,
      look,
      host.fit.heading,
      c.width / 2,
      c.height - 22,
      Math.floor(t / 250),
      c.height - 84,
    );
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** A small picture of the back skin: four violet scabbards fanned on a silver-rimmed mount. */
export function skinPreview(color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = 96;
  c.height = 96;
  c.className = 'skin-pic';
  const g = c.getContext('2d')!;
  const seg = (x0: number, y0: number, x1: number, y1: number, w: number, col: string) => {
    g.strokeStyle = col;
    g.lineWidth = w;
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(x1, y1);
    g.stroke();
  };
  g.fillStyle = `rgba(${color},.18)`;
  g.beginPath();
  g.arc(48, 46, 40, 0, Math.PI * 2);
  g.fill();
  for (const [ox, ang] of [
    [-10, -0.5],
    [-4, -0.17],
    [4, 0.17],
    [10, 0.5],
  ] as const) {
    const [dx, dy] = [Math.sin(ang), -Math.cos(ang)];
    const [x0, y0] = [48 + ox, 66];
    const [x1, y1] = [x0 + dx * 44, y0 + dy * 44];
    seg(x0, y0, x1, y1, 8, '#26143e');
    seg(x0, y0, x1, y1, 6, '#5a3a9a');
    seg(x0 - dy * 2, y0 + dx * 2, x1 - dy * 2, y1 + dx * 2, 1.5, '#9a7ade');
    seg(x1 - dy * 7, y1 + dx * 7, x1 + dy * 7, y1 - dx * 7, 3, '#e4e4f0');
    seg(x1, y1, x1 + dx * 12, y1 + dy * 12, 3, '#2a1a3a');
    g.fillStyle = '#3fe0c8';
    g.fillRect(x1 + dx * 14 - 2, y1 + dy * 14 - 2, 4, 4);
  }
  g.fillStyle = '#4a2a7a';
  g.beginPath();
  g.ellipse(48, 68, 24, 8, 0, 0, Math.PI);
  g.fill();
  g.strokeStyle = '#d8d8e8';
  g.lineWidth = 2;
  g.stroke();
  g.fillStyle = '#3fe0c8';
  g.fillRect(45, 69, 6, 4);
  for (const tx of [36, 60]) {
    g.fillStyle = '#3fe0c8';
    g.fillRect(tx - 1, 76, 3, 3);
    g.fillStyle = '#f2eef8';
    g.fillRect(tx - 2, 80, 5, 13);
  }
  return c;
}
